import type { Coordinate } from '@orgsim/shared';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CircleMarker, Polyline, useMap, useMapEvents } from 'react-leaflet';
import { fromLatLng, toLatLng } from './crs';
import { arrangeMarkerPoints } from './markerGeometry';

type Entry = { id: string; position: Coordinate; radius: number };
type Layout = { register: (entry: Entry) => () => void; positions: Record<string, Coordinate> };
const LayoutContext = createContext<Layout | null>(null);

export function MarkerLayout({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const map = useMap();
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const register = useCallback((entry: Entry) => {
    setEntries((prev) => ({ ...prev, [entry.id]: entry }));
    return () =>
      setEntries((prev) => {
        const { [entry.id]: removed, ...rest } = prev;
        return removed ? rest : prev;
      });
  }, []);
  const positions = useMemo(() => {
    if (!enabled) return {};
    const projected = Object.values(entries).map((entry) => ({
      ...map.project(toLatLng(entry.position), zoom),
      id: entry.id,
      radius: entry.radius,
    }));
    return Object.fromEntries(
      Object.entries(arrangeMarkerPoints(projected)).map(([id, point]) => [
        id,
        fromLatLng(map.unproject([point.x, point.y], zoom)),
      ]),
    );
  }, [entries, enabled, map, zoom]);
  const value = useMemo(() => ({ register, positions }), [register, positions]);
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useMarkerPosition(id: string, position: Coordinate, radius: number) {
  const layout = useContext(LayoutContext);
  const register = layout?.register;
  const { x, y } = position;
  useEffect(() => register?.({ id, position: { x, y }, radius }), [register, id, x, y, radius]);
  return layout?.positions[id] ?? position;
}

export function MarkerConnector({
  actual,
  displayed,
}: { actual: Coordinate; displayed: Coordinate }) {
  if (Math.hypot(actual.x - displayed.x, actual.y - displayed.y) < 0.1) return null;
  return (
    <>
      <Polyline
        positions={[toLatLng(actual), toLatLng(displayed)]}
        interactive={false}
        pathOptions={{ color: '#f1eee6', weight: 1.5, opacity: 0.8, dashArray: '3 5' }}
      />
      <CircleMarker
        center={toLatLng(actual)}
        radius={3}
        interactive={false}
        pathOptions={{ color: '#f1eee6', weight: 1, fillOpacity: 0.8 }}
      />
    </>
  );
}
